import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import {
  SBEF_BAR_LIST,
  PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR,
} from './report-product-groups.constants';

type ComparisonHorizontalBarProps = {
  /** Baseline as % of the container width (0–100). */
  baselinePct: number;
  /** Proposal as % of the container width (0–100). */
  proposalPct: number;
  /** Proposal bar fill color. */
  color: string;
  /** Corner radius scaling number — defaults to {@link SBEF_BAR_LIST.partRadius}. */
  partRadius?: number;
  /** Border width scaling number — defaults to {@link SBEF_BAR_LIST.partBorderWidth}. */
  partBorderWidth?: number;
};

/**
 * Horizontal comparison bar: grey baseline behind, colored proposal in front.
 * Fills its parent container — caller controls the container's width and height.
 */
export const ComparisonHorizontalBar: React.FC<
  ComparisonHorizontalBarProps
> = ({
  baselinePct,
  proposalPct,
  color,
  partRadius = SBEF_BAR_LIST.partRadius,
  partBorderWidth = SBEF_BAR_LIST.partBorderWidth,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({
    getScalingSize,
    partRadius,
    partBorderWidth,
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.baseline} sx={{ width: `${baselinePct}%` }} />
      <Box
        className={classes.fill}
        data-co2e-bar=""
        sx={{ width: `${proposalPct}%`, backgroundColor: color }}
      />
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  partRadius: number;
  partBorderWidth: number;
}>()((_theme, { getScalingSize, partRadius, partBorderWidth }) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  baseline: {
    position: 'absolute',
    boxSizing: 'border-box',
    top: 0,
    left: 0,
    height: '100%',
    zIndex: 1,
    backgroundColor: BASELINE_COLOR,
    borderRadius: `0 ${getScalingSize(partRadius)} ${getScalingSize(partRadius)} 0`,
    border: `${getScalingSize(partBorderWidth)} solid ${PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR}`,
  },
  fill: {
    position: 'absolute',
    boxSizing: 'border-box',
    top: 0,
    left: 0,
    height: '100%',
    zIndex: 2,
    borderRadius: `0 ${getScalingSize(partRadius)} ${getScalingSize(partRadius)} 0`,
    border: `${getScalingSize(partBorderWidth)} solid ${PRODUCT_GROUPS_DONUT_SLICE_INNER_BORDER_COLOR}`,
  },
}));
